// src/components/PDF/InteractiveLinkModal.js (NEW)
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  ExternalLink, 
  Globe, 
  Shield, 
  AlertTriangle, 
  RefreshCw, 
  ArrowLeft, 
  ArrowRight,
  Home,
  Maximize2,
  Minimize2,
  Copy,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';

const InteractiveLinkModal = ({ isOpen, onClose, linkUrl, linkText = 'Link' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUrl, setCurrentUrl] = useState(linkUrl);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentUrl(linkUrl);
      setIsLoading(true);
      setCanGoBack(false);
      setCanGoForward(false);
    }
  }, [isOpen, linkUrl]);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  if (!isOpen) return null;

  const getDomain = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return 'Unknown domain';
    }
  };

  const isSecureUrl = (url) => {
    try {
      return new URL(url).protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    toast.error('Failed to load website');
  };

  const refreshPage = () => {
    if (iframeRef.current) {
      setIsLoading(true);
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const goBack = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        iframeRef.current.contentWindow.history.back();
      } catch (e) {
        console.warn('Cannot navigate back:', e);
      }
    }
  };

  const goForward = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        iframeRef.current.contentWindow.history.forward();
      } catch (e) {
        console.warn('Cannot navigate forward:', e);
      }
    }
  };

  const goHome = () => {
    setCurrentUrl(linkUrl);
    setIsLoading(true);
  };

  const openInNewTab = () => {
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
    toast.success('Opened in new tab');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      toast.success('URL copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const secure = isSecureUrl(currentUrl);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
      isFullscreen ? 'p-0' : 'p-4'
    }`}>
      <div className={`bg-white flex flex-col shadow-xl ${
        isFullscreen 
          ? 'w-full h-full' 
          : 'rounded-lg max-w-6xl w-full max-h-[90vh]'
      }`}>
        {/* Browser-like Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          {/* Navigation Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={goBack}
              disabled={!canGoBack}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goForward}
              disabled={!canGoForward}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Go Forward"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={refreshPage}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={goHome}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
              title="Go to Original Link"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>

          {/* Address Bar */}
          <div className="flex-1 mx-4">
            <div className="flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2">
              <div className="flex items-center space-x-2 mr-3">
                {secure ? (
                  <Shield className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                )}
                <Globe className="w-4 h-4 text-gray-600" />
              </div>
              
              <div className="flex-1 text-sm text-gray-700 font-mono truncate">
                {currentUrl}
              </div>
              
              <button
                onClick={copyToClipboard}
                className="ml-2 p-1 text-gray-600 hover:text-gray-900 transition-colors"
                title="Copy URL"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Window Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </button>
            
            <button
              onClick={openInNewTab}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
              title="Open in New Tab"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Website Content */}
        <div className="flex-1 relative bg-white">
          {isLoading && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#232751] mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading {getDomain(currentUrl)}...</p>
              </div>
            </div>
          )}
          
          <iframe
            ref={iframeRef}
            src={currentUrl}
            className="w-full h-full border-0"
            style={{ 
              minHeight: isFullscreen ? 'calc(100vh - 73px)' : '500px'
            }}
            onLoad={handleLoad}
            onError={handleError}
            title={`Interactive view: ${linkText}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-navigation allow-popups-to-escape-sandbox"
          />
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
          <div className="flex items-center space-x-4">
            <span>Domain: {getDomain(currentUrl)}</span>
            <span className={secure ? 'text-green-600' : 'text-amber-600'}>
              {secure ? 'Secure (HTTPS)' : 'Not Secure (HTTP)'}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <span>Interactive Mode</span>
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveLinkModal;
